// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Dashboard.js
// Landing page after login. Tool cards (Charts = /chart, the new chart; Charts (old) = /chart-old backup), plus admin-only cards incl. AI Plane (/ai) and Trading (/trading).
import React from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import {
  LayoutGrid, Activity, Layers, GitCompare, Search, ChartCandlestick, ScanLine,
  FlaskConical, Home, PiggyBank, Settings, ShieldCheck, Database, Link2, Bot, Users, Pill, Newspaper, Briefcase, Calendar,
} from 'lucide-react';

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  nav: {
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    padding: '0 32px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.1em',
    color: 'var(--accent)',
    textTransform: 'uppercase',
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' },
  logoutBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--accent)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif",
    padding: '6px 12px', borderRadius: 'var(--radius)',
    transition: 'background 0.15s',
  },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '48px 32px' },
  welcome: { fontSize: '28px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub: { fontSize: '14px', color: 'var(--text-muted)', marginBottom: '48px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'calc(var(--radius) * 2)',
    padding: '28px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    textDecoration: 'none',
    display: 'block',
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' },
  cardTitle: { fontSize: '16px', fontWeight: 600, color: 'var(--text)' },
  cardDesc: { fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.6' },
};

const tools = [
  { title: 'Work Space', icon: LayoutGrid, desc: 'Market Scanner + Charts all contained in a single workspace.', path: '/workspace' },
  { title: 'Technical and Fundamental Analysis', icon: Activity, desc: 'Set up technical analysis scans based on your criteria.', path: '/scans' },
  { title: 'ETFs', icon: Layers, desc: 'Browse and filter ETFs with full holdings breakdown.', path: '/etf' },
  { title: 'Compare', icon: GitCompare, desc: 'Compare ETFs, stocks and indexes on a normalised % return chart with zoom and EMA overlays.', path: '/compare' },
  { title: 'Events Calendar', icon: Calendar, desc: 'Earnings, what companies said they would deliver, and dividend ex-dates, by day - filter by exchange, list, sector or ETF.', path: '/events' },
  { title: 'Index Performance', icon: Activity, desc: 'Rank all ASX and US market indexes by percentage price return, from one week to five years.', path: '/indexes' },
  { title: 'Market Breadth', icon: Activity, desc: 'Compare technical breadth across announcement-AI sectors, industry groups and commodities.', path: '/breadth' },
  { title: 'Scanner', icon: Search, desc: 'Filter and scan ASX and US stocks and ETFs by technical indicators, performance, and fundamentals.', path: '/scanner' },
  { title: 'Charts', icon: ChartCandlestick, desc: 'Interactive candlestick charts with technical analysis tools for ASX and US securities.', path: '/chart' },
  { title: 'Charts (old)', icon: ChartCandlestick, desc: 'The previous chart, kept as a backup while the new chart settles in. Will be removed.', path: '/chart-old' },
  { title: 'Live Mover Audit', icon: ChartCandlestick, desc: 'Review live US market-mover alerts with historical trigger-price rays and closing prices.', path: '/movers/audit' },
  { title: 'Company', icon: Briefcase, desc: 'Search ASX and US company profiles, financials, performance and announcements.', path: '/company' },
  { title: 'Announcements', icon: Newspaper, desc: 'Latest ASX and US company announcements as they are released, with AI summaries and importance ratings.', path: '/announcements' },
  { title: 'Analysts', icon: Users, desc: 'Analyst price targets and ratings, sortable by upside vs last price.', path: '/analysts' },
  { title: 'FDA Approvals', icon: Pill, desc: 'Latest drug approvals from Drugs@FDA, with sponsors matched to listed tickers.', path: '/fda' },
  { title: 'Strategy', icon: FlaskConical, desc: 'Build, backtest and employ DCA strategies with custom signal rules.', path: '/strategy' },
  { title: 'Mortgage Calculator', icon: Home, desc: 'Calculate how extra repayments reduce your loan term and total interest paid.', path: '/mortgage-calculator' },
  { title: 'Investment Calculator', icon: PiggyBank, desc: 'Model compound growth and DRIP returns over time for retirement planning.', path: '/investment-calculator' },
  { title: 'Settings', icon: Settings, desc: 'Manage your watchlists, annotation privacy, and account preferences.', path: '/settings' },
];

export default function Dashboard() {
  const { user } = useAuth();

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.content}>
        <h1 style={styles.welcome}>Good to see you, {user?.username}.</h1>
        <p style={styles.sub}>Nothing here is financial advice, and the maths is most certainly wrong.</p>
        <div style={styles.grid}>
          {[...tools, ...(user?.is_admin ? [
          { title: 'Admin Panel', icon: ShieldCheck, desc: 'Manage users, registrations, blacklist, and site settings.', path: '/admin' },
          { title: 'ETF Management', icon: Database, desc: 'Manage ETF providers, holdings URLs and ingestion status.', path: '/admin/etf' },
          { title: 'Leveraged Links', icon: Link2, desc: 'Curate leveraged/inverse ETF families and their underlyings.', path: '/admin/leveraged' },
          { title: 'AI Plane', icon: Bot, desc: 'AI assistant with direct read/write access to the codebase.', path: '/ai' },
          { title: 'Trading', icon: Briefcase, desc: 'Your broker connection: accounts, balances, holdings, orders and paper trading.', path: '/trading' },
           ] : [])].map(t => (
            <a key={t.path} href={t.path} style={styles.card}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-dim)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={styles.cardHead}>
                {t.icon && <t.icon size={26} strokeWidth={1.5} color="var(--accent)" />}
                <span style={styles.cardTitle}>{t.title}</span>
              </div>
              <div style={styles.cardDesc}>{t.desc}</div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
